import api from './client';

export interface CatalogItem {
  id: number;
  brand: string;
  model_name: string;
  norm_key: string;
  product_types: string[] | null;
  viscosity: string | null;
  base_type: string | null;
  specifications: string[] | null;
  description: string | null;
  verified: boolean;
  gtin?: string[] | null;
  onec_items?: { item_id: string; item_name: string | null }[];
  extra_data: Record<string, unknown> | null;
  ai_enriched: boolean;
  ai_confidence: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CatalogFilters {
  brand?: string;
  product_type?: string;
  viscosity?: string;
  base_type?: string;
  verified?: boolean;
  search?: string;
}

export interface CatalogPayload {
  brand: string;
  model_name: string;
  product_types?: string[] | null;
  viscosity?: string | null;
  base_type?: string | null;
  specifications?: string[] | null;
  description?: string | null;
  verified?: boolean;
  extra_data?: Record<string, unknown> | null;
  ai_enriched?: boolean;
  ai_confidence?: number | null;
}

export interface UnclassifiedProduct {
  id: number;
  brand: string;
  model_name: string;
  norm_key: string;
  product_types: string[];
}

export interface UnclassifiedFilters {
  supplier_id?: number;
}

export interface AuditCatalogInfo {
  id: number;
  brand: string;
  model_name: string;
  product_types: string[] | null;
  verified: boolean;
  ai_enriched: boolean;
  ai_confidence: number | null;
}

export interface AuditItem {
  oil_product_id: number;
  supplier_id: number;
  source_brand: string;
  source_model_name: string;
  source_product_types: string[];
  source_norm_key: string;
  catalog_id: number | null;
  issue_flags: string[];
  catalog: AuditCatalogInfo | null;
}

export interface AuditFilters {
  supplier_id: number;
  only_ai?: boolean;
  only_issues?: boolean;
}

export const getCatalog = (filters?: CatalogFilters) =>
  api.get<CatalogItem[]>('/product-catalog', { params: filters }).then(r => r.data);

export const getCatalogItem = (id: number) =>
  api.get<CatalogItem>(`/product-catalog/${id}`).then(r => r.data);

export const createCatalogItem = (data: CatalogPayload) =>
  api.post<CatalogItem>('/product-catalog', data).then(r => r.data);

export const updateCatalogItem = (id: number, data: Partial<CatalogPayload>) =>
  api.put<CatalogItem>(`/product-catalog/${id}`, data).then(r => r.data);

export const deleteCatalogItem = (id: number) =>
  api.delete(`/product-catalog/${id}`);

export const getUnclassified = (filters?: UnclassifiedFilters) =>
  api.get<UnclassifiedProduct[]>('/product-catalog/unclassified', { params: filters }).then(r => r.data);

export const getAuditItems = (filters: AuditFilters) =>
  api.get<AuditItem[]>('/product-catalog/audit', { params: filters }).then(r => r.data);

export const linkProduct = (catalogId: number, oilProductId: number) =>
  api.post(`/product-catalog/${catalogId}/link/${oilProductId}`).then(r => r.data);

export interface AiAnalysisResult {
  brand: string;
  model_name: string;
  product_types: string[] | null;
  viscosity: string | null;
  base_type: string | null;
  keywords: string[];
  confidence: number;
}

export interface EnrichBatchRequest {
  all_unclassified?: boolean;
  product_ids?: number[];
  supplier_id?: number;
}

export const analyzeProducts = (names: string[]) =>
  api.post<AiAnalysisResult[]>('/product-catalog/analyze', { names }).then(r => r.data);

export const getProductTypes = () =>
  api.get<string[]>('/product-catalog/product-types').then(r => r.data);

export const getCatalogBrands = () =>
  api.get<string[]>('/product-catalog/brands').then(r => r.data);

export const enrichBatch = (req: EnrichBatchRequest) =>
  api.post<{ enriched: number }>('/product-catalog/enrich-batch', req).then(r => r.data);

export interface DuplicateCandidate {
  id: number;
  brand: string;
  model_name: string;
  norm_key: string;
  product_types: string[] | null;
  exact: boolean;
}

export const findDuplicates = (brand: string, modelName: string) =>
  api.post<DuplicateCandidate[]>('/product-catalog/find-duplicates', { brand, model_name: modelName }).then(r => r.data);
